import { Map as MaplibreMap, type StyleSpecification } from 'maplibre-gl';

/**
 * Shared OSM raster tile style used by every map in the app.
 * Kept here so there is exactly one tile source / attribution definition.
 */
export const osmStyle: StyleSpecification = {
  version: 8,
  sources: {
    osm: {
      type: 'raster',
      tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
      tileSize: 256,
      attribution: '© OpenStreetMap contributors',
    },
  },
  layers: [
    {
      id: 'osm',
      type: 'raster',
      source: 'osm',
    },
  ],
} as const;

export interface CreateOsmMapOptions {
  center: [number, number]; // [lng, lat]
  zoom: number;
}

/**
 * Force MapLibre to remeasure its container.
 * Safe to call after remove — swallows post-teardown errors.
 */
export function resizeOsmMap(map: MaplibreMap): void {
  try {
    map.resize();
  } catch {
    // Map already removed or WebGL context gone.
  }
}

/**
 * Create a MapLibre GL map with the shared OSM raster style.
 *
 * Modeled on the proven MapPinPicker initialization: a single
 * `new MaplibreMap()` call with a try/catch boundary owned by the
 * caller. No WebGL pre-check gate, no error-classification heuristics,
 * no context-loss remount loops — just create and return.
 *
 * Also schedules resize passes so a flex/absolute container that is
 * still 0×0 at construction time gets a real canvas once layout settles
 * (common in full-page explorer shells and CDF preview iframes).
 *
 * @throws if MapLibre cannot construct the map (e.g. WebGL unavailable).
 *         The caller is expected to catch and show a fallback UI.
 */
export function createOsmMap(
  container: HTMLElement,
  options: CreateOsmMapOptions,
): MaplibreMap {
  // Prefer an explicit size when the host only has percentage height.
  // MapLibre samples clientWidth/clientHeight once at construct time.
  if (container.clientHeight === 0 || container.clientWidth === 0) {
    container.style.minHeight = container.style.minHeight || '240px';
    container.style.minWidth = container.style.minWidth || '100%';
  }

  const map = new MaplibreMap({
    container,
    style: osmStyle,
    center: options.center,
    zoom: options.zoom,
  });

  const resize = () => resizeOsmMap(map);
  requestAnimationFrame(() => {
    resize();
    requestAnimationFrame(resize);
  });
  map.once('load', resize);

  return map;
}
